/**
 * Autoplay-in-view for the "Video" section (sections/video.liquid, autoplay mode).
 *
 * The <video> is rendered muted inside a [data-video-autoplay] wrapper. This script plays it when the
 * section scrolls into view and pauses it when it leaves, so the customer never has to click.
 * It also resumes playback after theme modals close (the theme calls pauseAllMedia() on every <video>)
 * and after the tab becomes visible again.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-video-autoplay]';

  function safePlay(video) {
    if (!video) return;
    // Browsers only allow autoplay without a user gesture when the video is muted.
    if (!video.dataset.videoAutoplayUserSound) video.muted = true;
    var promise = video.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(function () {
        // Autoplay blocked (iOS Low Power Mode, data saver). The first touch/scroll unlocks muted
        // playback, so retry once on that gesture instead of leaving a dead black box.
        armGestureRetry(video);
      });
    }
  }

  function armGestureRetry(video) {
    if (video.dataset.videoAutoplayRetryArmed) return;
    video.dataset.videoAutoplayRetryArmed = 'true';
    var events = ['touchend', 'pointerdown', 'scroll', 'keydown'];
    function retry() {
      events.forEach(function (name) {
        document.removeEventListener(name, retry, true);
      });
      delete video.dataset.videoAutoplayRetryArmed;
      if (video.dataset.videoAutoplayInView === 'true' && video.paused) safePlay(video);
    }
    events.forEach(function (name) {
      document.addEventListener(name, retry, { capture: true, passive: true });
    });
  }

  function initWrapper(wrapper) {
    if (wrapper.dataset.videoAutoplayReady) return;
    var video = wrapper.querySelector('video');
    if (!video) return;
    wrapper.dataset.videoAutoplayReady = 'true';

    video.setAttribute('muted', '');
    video.muted = true;
    video.setAttribute('playsinline', '');
    video.playsInline = true;

    if (wrapper.dataset.videoNoControls !== undefined) {
      // Plain autoplay clip: no native controls, picture-in-picture, casting, download or context menu.
      video.controls = false;
      video.removeAttribute('controls');
      video.disablePictureInPicture = true;
      video.setAttribute('disablepictureinpicture', '');
      video.disableRemotePlayback = true;
      video.setAttribute('disableremoteplayback', '');
      video.setAttribute('controlslist', 'nodownload nofullscreen noremoteplayback');
      video.addEventListener('contextmenu', function (event) {
        event.preventDefault();
      });
    }

    // If the customer turns the sound on through the controls, keep it on for later re-plays.
    video.addEventListener('volumechange', function () {
      if (!video.muted) {
        video.dataset.videoAutoplayUserSound = 'true';
      } else {
        delete video.dataset.videoAutoplayUserSound;
      }
    });

    var inView = false;

    if ('IntersectionObserver' in window) {
      var observer = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            inView = entry.isIntersecting;
            video.dataset.videoAutoplayInView = inView ? 'true' : 'false';
            if (inView) {
              safePlay(video);
            } else if (!video.paused) {
              video.pause();
            }
          });
        },
        { threshold: 0.35, rootMargin: '0px 0px 120px 0px' }
      );
      observer.observe(wrapper);
    } else {
      inView = true;
      video.dataset.videoAutoplayInView = 'true';
      safePlay(video);
    }

    function resumeIfVisible() {
      if (inView && video.paused && !document.hidden) safePlay(video);
    }

    // Theme modals (product media modal, etc.) pause every <video> when they open/close.
    document.body.addEventListener('modalClosed', function () {
      window.setTimeout(resumeIfVisible, 50);
    });
    document.addEventListener('visibilitychange', resumeIfVisible);
  }

  function init(root) {
    (root || document).querySelectorAll(SELECTOR).forEach(initWrapper);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  // Theme editor re-renders the section.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
